/**
 * Same-pod TUI/shell child supervisor. When the API parks an extra interactive
 * session on this (builder) pod and pushes `session:spawnTui`, the supervisor
 * spawns another conveyor-agent process — same CLI binary, same repo checkout —
 * bound to it via env overrides (CONVEYOR_MODE "adhoc" = a second Claude TUI,
 * "shell" = a raw login shell). Each child owns its own AgentConnection/PTY
 * stream, so a new terminal tab appears through the normal multi-session relay
 * with no relay changes.
 *
 * Multi-slot (unlike ReviewChildSupervisor): tabs are additive — several TUI/
 * shell children can coexist, keyed by their sessionId. The server caps how
 * many it will park per workspace. Spawn failures are reported back
 * (`reportSessionSpawnFailure`), which Ends the orphaned session; there is no
 * fallback pod (a spawned tab only makes sense on this pod).
 *
 * Shared-checkout caveat (deliberate, same as the review child): children
 * operate on the builder's working tree. They never run setup/start commands
 * and never touch git themselves on spawn.
 */
import { spawn as nodeSpawn, type ChildProcess } from "node:child_process";
import type { SpawnTuiData } from "../connection/agent-connection.js";
import { createServiceLogger } from "../utils/logger.js";
import { KILL_WAIT_MS, SPAWN_FAILURE_GRACE_MS, type SpawnFn } from "./review-child.js";

const logger = createServiceLogger("SessionChild");

export interface SessionChildConnection {
  reportSessionSpawnFailure(spawnedSessionId: string, error?: string): void;
}

/**
 * Build the child env from the builder's env. The child is the same CLI in
 * adhoc/shell mode, so it reuses the pod's credentials while swapping in the
 * spawned session's identity.
 */
export function buildSpawnedChildEnv(
  baseEnv: NodeJS.ProcessEnv,
  data: SpawnTuiData,
): NodeJS.ProcessEnv {
  const env: NodeJS.ProcessEnv = { ...baseEnv };
  // The spawned session's identity: its own JWT + sessionId + runner mode.
  // CONVEYOR_TASK_ID stays the parent's — same task (SEC-9 derives the task
  // from the JWT server-side). CONVEYOR_PROJECT_ID feeds the adhoc runner's
  // connected-event payload.
  env.CONVEYOR_TASK_TOKEN = data.sessionJwt;
  env.CONVEYOR_SESSION_ID = data.sessionId;
  env.CONVEYOR_MODE = data.mode;
  env.CONVEYOR_PROJECT_ID = data.projectId;

  // The bootstrap-refresh path would replace the child's JWT with the
  // BUILDER's writer JWT (findBundleSession prefers the live writer), silently
  // re-identifying the child as the builder — remove the tokens (same as the
  // review child; the 24h JWT TTL outlives any tab).
  delete env.POD_BOOTSTRAP_TOKEN;
  delete env.CONVEYOR_BOOTSTRAP_TOKEN;

  // The builder already ran (or is running) setup/start on this shared
  // workspace — the child must never re-run `bun install` or spawn a second
  // dev server.
  delete env.CONVEYOR_SETUP_COMMAND;
  delete env.CONVEYOR_START_COMMAND;

  // Adhoc/shell runners have no agent-mode axis; a stale builder hint must
  // not leak in.
  delete env.CONVEYOR_AGENT_MODE;
  delete env.CONVEYOR_IS_AUTO;
  return env;
}

export class SessionChildSupervisor {
  private readonly children = new Map<string, ChildProcess>();

  constructor(
    private readonly connection: SessionChildConnection,
    private readonly workspaceDir: string,
    private readonly spawnFn: SpawnFn = nodeSpawn as SpawnFn,
  ) {}

  /** Live child count (for tests/diagnostics). */
  get size(): number {
    return this.children.size;
  }

  /** Spawn a TUI/shell child for a `session:spawnTui` push. Additive per session. */
  async spawn(data: SpawnTuiData): Promise<void> {
    logger.info("Spawning same-pod session child", {
      spawnedSessionId: data.sessionId,
      mode: data.mode,
    });
    // A duplicate push for the same session replaces its own slot only.
    await this.stopChild(data.sessionId, "superseded by duplicate spawn");

    const env = buildSpawnedChildEnv(process.env, data);
    const cliPath = process.argv[1];
    if (!cliPath) {
      this.connection.reportSessionSpawnFailure(data.sessionId, "cannot resolve CLI entry path");
      return;
    }

    let child: ChildProcess;
    try {
      child = this.spawnFn(process.execPath, [cliPath], {
        cwd: this.workspaceDir,
        env,
        stdio: ["ignore", "pipe", "pipe"],
      });
    } catch (err) {
      this.connection.reportSessionSpawnFailure(
        data.sessionId,
        err instanceof Error ? err.message : String(err),
      );
      return;
    }

    this.children.set(data.sessionId, child);
    const spawnedAt = Date.now();
    let reported = false;
    const reportOnce = (message: string): void => {
      if (reported) return;
      reported = true;
      this.connection.reportSessionSpawnFailure(data.sessionId, message);
    };

    // Child output lands in the pod log (the entrypoint tees our stdio),
    // prefixed so builder and tab streams stay distinguishable.
    const prefix = `[${data.mode}-child]`;
    child.stdout?.on("data", (chunk: Buffer) => {
      process.stdout.write(`${prefix} ${chunk.toString()}`);
    });
    child.stderr?.on("data", (chunk: Buffer) => {
      process.stderr.write(`${prefix} ${chunk.toString()}`);
    });

    child.on("error", (err) => {
      logger.error("Session child spawn error", { error: err.message });
      if (this.children.get(data.sessionId) === child) {
        this.children.delete(data.sessionId);
      }
      reportOnce(err.message);
    });

    child.on("exit", (code, signal) => {
      const wasCurrent = this.children.get(data.sessionId) === child;
      if (wasCurrent) this.children.delete(data.sessionId);
      logger.info("Session child exited", {
        code,
        signal,
        spawnedSessionId: data.sessionId,
        mode: data.mode,
      });
      // A non-zero exit right after launch means the child never became the
      // tab's agent — End the orphaned session server-side. Later exits (user
      // typed `exit`, server-sent session:stop, supersede kill) are normal
      // lifecycle: the server owns the session state there.
      const withinGrace = Date.now() - spawnedAt < SPAWN_FAILURE_GRACE_MS;
      if (wasCurrent && withinGrace && code !== null && code !== 0) {
        reportOnce(`session child exited with code ${code} within startup grace`);
      }
    });
  }

  /** Stop every live child (parent shutdown). Best-effort. */
  async stopAll(): Promise<void> {
    const ids = [...this.children.keys()];
    await Promise.all(ids.map((id) => this.stopChild(id, "parent shutting down")));
  }

  private async stopChild(sessionId: string, reason: string): Promise<void> {
    const child = this.children.get(sessionId);
    this.children.delete(sessionId);
    if (!child || child.exitCode !== null || child.killed) return;
    logger.info("Stopping session child", { reason, spawnedSessionId: sessionId });

    await new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        try {
          child.kill("SIGKILL");
        } catch {
          /* already gone */
        }
        resolve();
      }, KILL_WAIT_MS);
      timer.unref();
      child.once("exit", () => {
        clearTimeout(timer);
        resolve();
      });
      try {
        child.kill("SIGTERM");
      } catch {
        clearTimeout(timer);
        resolve();
      }
    });
  }
}
